import type { HudStore } from "./hud/store.ts";
import { writeBarKeys, type SettingsStore } from "./settings.ts";

/**
 * The page's Show key numbers on the bar choice (P2.18-T15, the author's answer of 2026-10-08: the bar draws no key
 * numbers unless the viewer asks for them), as `HealthNumbersSetting` holds Show health numbers: made from what the
 * store holds as the page loads, put in the HUD's store, which the ability bar reads, and taking each change Settings
 * makes, which applies at once and is kept per device.
 *
 * @example
 * ```ts
 * const barKeys = new BarKeysSetting(hud, readBarKeys());
 * barKeys.setShow(true);
 * ```
 */
export class BarKeysSetting {
  /**
   * @param hud The HUD's store, which the ability bar reads.
   * @param show Whether the keys draw as the page loads, as `readBarKeys` gives it.
   * @param store Where a change is kept; this browser's `localStorage` when omitted.
   */
  constructor(
    private readonly hud: Pick<HudStore, "setBarKeys" | "getSnapshot">,
    show: boolean,
    private readonly store?: SettingsStore | null,
  ) {
    hud.setBarKeys(show);
  }

  /**
   * Whether the bar's slots draw their keys now.
   *
   * @returns The setting.
   */
  get show(): boolean {
    return this.hud.getSnapshot().barKeys;
  }

  /**
   * Takes the viewer's choice, which the bar shows at once, and keeps it.
   *
   * @param show Whether the keys draw.
   */
  setShow(show: boolean): void {
    this.hud.setBarKeys(show);
    writeBarKeys(show, this.store);
  }
}
